import { Project } from '@/app/api';
import { pushAdditionalGaIds } from '@/app/hooks/useAnalytics/push-additional-ga-ids';
import { pushToDataLayer } from '@/app/hooks/useAnalytics/push-to-data-layer';
import {
  CheckoutStepNumbers,
  CheckoutSteps,
  CheckoutTypes,
  getProjectBrand,
  getProjectCategory,
  GoogleTagManagerEventNames,
  PageTypes,
} from '@/app/hooks/useAnalytics/types';
import { envs } from '@/app/utils/envs';
import mixpanel from 'mixpanel-browser';

export const publishMixpanelDonationFormPageViewEvent = (project: Project, abGroup: string, userId?: string) => {
  if (!envs.isMixpanelEnabled) {
    if (envs.isAnalyticInDebugMode) {
      console.log('Skipping Mixpanel - DonationFormPageViewEvent');
    }
    return;
  }
  const pageviewObject = {
    page_type: PageTypes.Checkout,
    checkout_type: project.settings.isGoodHabit ? CheckoutTypes.Subscription : CheckoutTypes.OneTime,
    step_name: CheckoutSteps.DonationForm,
    step_number: CheckoutStepNumbers.DonationForm,
    project_id: project.numericId,
    project_name: project.content.baseName,
    project_mode: project.settings.modeGroup,
    project_main_category: project.settings.taxonomy.mainCategory?.name,
    project_categories: project.settings.taxonomy.categories?.map((category) => category.name),
    topic: project.settings.taxonomy.topic?.name,
    abGroup,
    // TODO mixpanel.identify workaround
    $user_id: userId,
  };

  if (envs.isAnalyticInDebugMode) {
    console.log('Mix panel - DonationFormPageViewEvent', pageviewObject);
  }

  mixpanel.track_pageview(pageviewObject);
};

export const publishGtmDonationFormPageViewEvent = (project: Project, abGroup: string) => {
  if (!envs.isGtmEnabled) {
    if (envs.isAnalyticInDebugMode) {
      console.log('Skipping Google Tag Manager - DonationFormPageViewEvent');
    }
    return;
  }

  const gtmObject = {
    ecommerce: {
      items: [
        {
          item_id: project.numericId,
          item_name: project.content.baseName,
          item_brand: getProjectBrand(project),
          item_category: getProjectCategory(project),
          abGroup,
        },
      ],
    },
  };

  if (envs.isAnalyticInDebugMode) {
    console.log('Google Tag Manager - DonationFormPageViewEvent', gtmObject);
  }

  pushAdditionalGaIds(GoogleTagManagerEventNames.BeginCheckout, project);
  pushToDataLayer(gtmObject);
};

type PushDonationFormPageViewEventParams = {
  project: Project;
  userId?: string;
  abGroup: string;
  areStatisticsCookiesAllowed: boolean;
};

export const pushDonationFormPageViewEvent = ({
  abGroup,
  areStatisticsCookiesAllowed,
  project,
  userId,
}: PushDonationFormPageViewEventParams) => {
  if (!areStatisticsCookiesAllowed) {
    if (envs.isAnalyticInDebugMode) {
      console.log('Skiping DonationFormPageViewEvent, because statistics cookies are not allowed');
    }
    return;
  }
  publishGtmDonationFormPageViewEvent(project, abGroup);
  publishMixpanelDonationFormPageViewEvent(project, abGroup, userId);
};
